import React, { useState, useRef, useEffect, useMemo } from "react";
import { useColorMode } from "@chakra-ui/react";
import { getThemeClasses } from "../../../utils/themeUtils";
import ReactDOM from "react-dom";
import {
  Box,
  Text,
  Flex,
  VStack,
  Button,
  IconButton,
  Modal,
  ModalOverlay,
  ModalContent,
  ModalHeader,
  ModalCloseButton,
  ModalBody,
  Stat,
  StatLabel,
  StatNumber,
  StatHelpText,
  Divider,
  useDisclosure,
} from "@chakra-ui/react";
import { FaChartBar, FaChartLine, FaUser, FaPlus } from "react-icons/fa";
import UserModal from "./UserList";
import UserMetricsForm from "./UserMetricsForm";
import UserMetricsTable from "./UserMetricsTable";
import KPISummaryModal from "./KPISummaryModal";
import { IconWrapper } from "../../../components/IconWrapper";

export type User = {
  id: string;
  firstName: string;
  lastName: string;
  email: string;
  role: string;
  permissions?: "Admin" | "Editor" | "Viewer";
};

export type Metric = {
  id: number;
  metric: string;
  description?: string;
  target: string;
  actual: string;
  status: string;
  type?: string;
  createdBy?: string;
};

interface UserManagementProps {
  users: User[];
  color: string;
  bg: string;
  organizationId: string;
  isAdmin: boolean;
  isEditor?: boolean;
  canCreate?: boolean;
  currentUserId: string;
}

const UserManagement: React.FC<UserManagementProps> = ({
  users,
  color,
  bg,
  organizationId,
  isAdmin,
  isEditor = false,
  canCreate = false,
  currentUserId,
}) => {
  const { colorMode } = useColorMode();
  const isDark = colorMode === "dark";
  const theme = getThemeClasses(isDark);
  const { isOpen, onOpen, onClose } = useDisclosure();
  const {
    isOpen: isKpiOpen,
    onOpen: onKpiOpen,
    onClose: onKpiClose,
  } = useDisclosure();
  const {
    isOpen: isMetricsFormOpen,
    onOpen: onMetricsFormOpen,
    onClose: onMetricsFormClose,
  } = useDisclosure();
  const [selectedUser, setSelectedUser] = useState<User | null>(null);
  const [userMetrics, setUserMetrics] = useState<{
    [userId: string]: Metric[];
  }>({});
  const [showDropdown, setShowDropdown] = useState(false);
  const dropdownButtonRef = useRef<HTMLButtonElement>(null);
  const [dropdownPosition, setDropdownPosition] = useState<{ left: number; top: number; width: number } | null>(null);
  const [showFullPageKPI, setShowFullPageKPI] = useState(false);
  const [userSearchTerm, setUserSearchTerm] = useState("");
  const [search, setSearch] = useState("");

  // Get current user's metrics
  const currentMetrics = useMemo(
    () => (selectedUser ? userMetrics[selectedUser.id] || [] : []),
    [userMetrics, selectedUser]
  );

  // Filter metrics based on search term
  const filteredMetrics = useMemo(
    () =>
      currentMetrics.filter(
        (m) =>
          m.metric.toLowerCase().includes(search.toLowerCase()) ||
          (m.description &&
            m.description.toLowerCase().includes(search.toLowerCase())) ||
          String(m.target).toLowerCase().includes(search.toLowerCase()) ||
          String(m.actual).toLowerCase().includes(search.toLowerCase()) ||
          m.status.toLowerCase().includes(search.toLowerCase())
      ),
    [currentMetrics, search]
  );

  // Filter users based on search term
  const filteredUsers = users.filter(user =>
    user.firstName.toLowerCase().includes(userSearchTerm.toLowerCase()) ||
    user.lastName.toLowerCase().includes(userSearchTerm.toLowerCase()) ||
    `${user.firstName} ${user.lastName}`.toLowerCase().includes(userSearchTerm.toLowerCase())
  );

  useEffect(() => {
    if (showDropdown && dropdownButtonRef.current) {
      const rect = dropdownButtonRef.current.getBoundingClientRect();
      setDropdownPosition({
        left: rect.left,
        top: rect.bottom + window.scrollY,
        width: rect.width,
      });
    } else {
      // Clear search when dropdown closes
      setUserSearchTerm("");
    }
  }, [showDropdown]);

  useEffect(() => {
    if (users.length === 0) return;
    if (isAdmin) {
      if (!selectedUser) setSelectedUser(users[0]);
    } else {
      const me = users.find((u) => u.id === currentUserId) || users[0];
      setSelectedUser(me);
    }
  }, [users, isAdmin, currentUserId]);

  const addMetric = (userId: string, newMetric: Omit<Metric, "id">) => {
    setUserMetrics((prev) => ({
      ...prev,
      [userId]: [...(prev[userId] || []), { ...newMetric, id: Date.now() }],
    }));
  };

  const updateMetric = (userId: string, updatedMetric: Metric) => {
    setUserMetrics((prev) => ({
      ...prev,
      [userId]: prev[userId].map((m) =>
        m.id === updatedMetric.id ? updatedMetric : m
      ),
    }));
  };

  const deleteMetric = (userId: string, metricId: number) => {
    setUserMetrics((prev) => ({
      ...prev,
      [userId]: prev[userId].filter((m) => m.id !== metricId),
    }));
  };

  // Helper to get user initials
  function getInitials(user: User) {
    const names = `${user.firstName} ${user.lastName}`.trim().split(" ");
    if (names.length === 1) return names[0].slice(0, 2).toUpperCase();
    return (names[0][0] + names[names.length - 1][0]).toUpperCase();
  }

  if (showFullPageKPI) {
    return (
      <KPISummaryModal
        isOpen={true}
        onClose={() => setShowFullPageKPI(false)}
        selectedUser={selectedUser}
        userMetrics={userMetrics}
        bg={bg}
        color={color}
        fullPage={true}
      />
    );
  }

  return (
    <Flex
      direction="column"
      width="100%"
      flex="1"
      minH="0"
      className="text-inherit"
    >
      {/* User Selection Row - Always visible */}
      <div className="flex items-center justify-between px-4 py-2 w-full">
        <div className="flex items-center gap-2">
          <span className={`font-bold text-base ${isDark ? 'text-white' : 'text-gray-900'}`}>
            {selectedUser ? `KPI Metrics for ${selectedUser.firstName} ${selectedUser.lastName}` : "KPI Metrics"}
          </span>
          {selectedUser && canCreate && (
            <button
              onClick={onMetricsFormOpen}
              className={`inline-flex items-center justify-center w-6 h-6 rounded-full ${theme.primaryButton} transition-all duration-200 shadow-sm hover:shadow-md transform hover:scale-105`}
              aria-label="Add KPI Metric"
            >
              <IconWrapper icon={FaPlus} size={12} />
            </button>
          )}
        </div>
        <div className="flex gap-3 items-center">
          {isAdmin && users.length > 0 ? (
            <div className="relative">
              <button
                type="button"
                ref={dropdownButtonRef}
                onClick={() => setShowDropdown((prev) => !prev)}
                className={`w-56 text-left px-4 py-1.5 rounded-lg text-sm font-normal shadow-sm focus:outline-none flex items-center justify-between gap-2 border overflow-hidden ${isDark ? 'bg-[#14161B] text-white border-[#2E2F33] focus:ring-2 focus:ring-[#B59143]' : 'bg-white text-gray-800 border-gray-300 focus:ring-2 focus:ring-[#B59143]'}`}
              >
                {selectedUser ? (
                  <span className="flex items-center gap-2 min-w-0">
                    <span className={`inline-flex items-center justify-center w-7 h-7 rounded-full flex-shrink-0 ${isDark ? 'bg-[#2A3441] text-[#FBD282]' : 'bg-[#FFF7E6] text-[#B59143]'} font-semibold text-xs`}>
                      {getInitials(selectedUser)}
                    </span>
                    <span className="truncate">{selectedUser.firstName} {selectedUser.lastName}</span>
                  </span>
                ) : (
                  <span className={`${isDark ? 'text-gray-400' : 'text-gray-400'}`}>Select a User</span>
                )}
                <svg className="w-4 h-4 ml-auto text-gray-400" fill="none" stroke="currentColor" strokeWidth="2" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" d="M19 9l-7 7-7-7" /></svg>
              </button>
              {showDropdown && dropdownPosition && ReactDOM.createPortal(
                <div
                  className={`${isDark ? 'bg-[#14161B] border-[#2E2F33] text-white' : 'bg-white border-gray-200 text-gray-800'} rounded-lg shadow-lg py-1 max-h-72 overflow-y-auto z-50`}
                  style={{
                    position: "absolute",
                    left: dropdownPosition.left,
                    top: dropdownPosition.top,
                    width: dropdownPosition.width,
                  }}
                >
                  <div className={`px-3 py-2 border-b ${isDark ? 'border-[#2E2F33]' : 'border-gray-100'}`}>
                    <div className="relative">
                      <input
                        type="text"
                        placeholder="Search by name..."
                        value={userSearchTerm}
                        onChange={(e) => setUserSearchTerm(e.target.value)}
                        className={`w-full px-3 py-2 pr-8 text-sm rounded-md focus:outline-none ${isDark ? 'bg-[#0B0B0F] text-white border border-[#2E2F33] focus:ring-2 focus:ring-[#B59143] focus:border-[#B59143]' : 'text-black border border-gray-200 focus:ring-2 focus:ring-[#B59143] focus:border-[#B59143]'}`}
                        onClick={(e) => e.stopPropagation()}
                      />
                      {userSearchTerm && (
                        <button
                          type="button"
                          onClick={(e) => {
                            e.stopPropagation();
                            setUserSearchTerm("");
                          }}
                          className={`absolute right-2 top-1/2 transform -translate-y-1/2 ${isDark ? 'text-gray-400 hover:text-gray-200' : 'text-gray-400 hover:text-gray-600'} focus:outline-none`}
                          title="Clear search"
                        >
                          <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                          </svg>
                        </button>
                      )}
                    </div>
                  </div>
                  {filteredUsers.length > 0 ? (
                    filteredUsers.map((user) => (
                      <button
                        key={user.id}
                        className={`flex items-center w-full px-4 py-2 gap-2 ${isDark ? 'hover:bg-[#2A3441]' : 'hover:bg-[#FFF7E6]'} text-sm transition border-b ${isDark ? 'border-[#2E2F33]' : 'border-gray-100'} last:border-b-0`}
                        onClick={() => {
                          setSelectedUser(user);
                          setShowDropdown(false);
                          setUserSearchTerm("");
                        }}
                      >
                        <span className={`inline-flex items-center justify-center w-7 h-7 rounded-full ${isDark ? 'bg-[#2A3441] text-[#FBD282]' : 'bg-[#FFF7E6] text-[#B59143]'} font-semibold text-xs`}>
                          {getInitials(user)}
                        </span>
                        <span>{user.firstName} {user.lastName}</span>
                      </button>
                    ))
                  ) : (
                    <div className={`px-4 py-3 text-sm ${isDark ? 'text-gray-400' : 'text-gray-500'} text-center`}>
                      No users found
                    </div>
                  )}
                </div>,
                document.body
              )}
            </div>
          ) : null}
          {selectedUser && (
            <IconButton
              icon={<IconWrapper icon={FaChartLine} size={13} />}
              aria-label="View KPI Summary"
              onClick={() => setShowFullPageKPI(true)}
              variant="outline"
              size="sm"
              height="36px"
              color="#B59143"
              borderColor="#B59143"
              borderWidth="2px"
              bg="transparent"
              _hover={{ bg: 'transparent', color: '#FBD282', borderColor: '#B59143' }}
              _active={{ bg: 'transparent' }}
            />
          )}
        </div>
      </div>

      {/* KPI Metrics Table Section */}
      {selectedUser && (
        <Box flex="1" minH="0" display="flex" flexDirection="column">
          <div className={`w-full rounded-md border ${theme.border} ${theme.card}`}>
            <div className={`${isDark ? 'bg-[#1A1C1F] border border-[#2E2F33] shadow-[0_6px_24px_rgba(0,0,0,0.35)]' : 'bg-white border border-gray-200 shadow-sm'} rounded-lg flex-1 min-h-0 flex flex-col`}>
              <div className={`flex justify-between items-center px-4 py-3 border-b ${isDark ? 'border-[#2E2F33]' : 'border-gray-100'}`}>
                <div className="relative">
                  <input
                    type="text"
                    placeholder="Search metrics..."
                    value={search}
                    onChange={(e) => setSearch(e.target.value)}
                    className={`w-64 px-3 py-1.5 text-xs rounded-md transition-all duration-200 outline-none ${isDark ? 'bg-[#0B0B0F] text-white border border-[#2E2F33] placeholder-[#C3C9D3] focus:ring-[#B59143] focus:border-[#B59143] hover:border-[#B59143]' : 'bg-white text-[#111827] border border-[#E5E7EB] placeholder-gray-500 focus:ring-[#B59143] focus:border-[#B59143] hover:border-[#B59143]'}`}
                  />
                </div>
                <span className={`inline-flex items-center px-3 py-1 rounded-full text-xs font-semibold ${isDark ? 'text-[#C3C9D3] border border-[#4A4D54]' : 'text-gray-600 border border-gray-300'}`}>
                  {currentMetrics.length} metrics
                </span>
              </div>
              <div className="p-4">
                <UserMetricsTable
                  organizationId={organizationId}
                  user={selectedUser}
                  metrics={currentMetrics}
                  filteredMetrics={filteredMetrics}
                  setUserMetrics={setUserMetrics}
                  updateMetric={updateMetric}
                  deleteMetric={deleteMetric}
                  color={color}
                  bg={bg}
                  currentUserId={currentUserId}
                  users={users}
                />
              </div>
            </div>
          </div>
        </Box>
      )}

      <Modal isOpen={isMetricsFormOpen} onClose={onMetricsFormClose} size="xl">
        <ModalOverlay />
        <ModalContent bg={isDark ? '#1A1C1F' : 'white'} color={isDark ? 'white' : 'inherit'} borderRadius="xl">
          <ModalCloseButton />
          <ModalBody pb={6}>
            {selectedUser && (
              <UserMetricsForm
                organizationId={organizationId}
                user={selectedUser}
                addMetric={addMetric}
                color={color}
                bg={bg}
                onClose={onMetricsFormClose}
                currentUserId={currentUserId}
              />
            )}
          </ModalBody>
        </ModalContent>
      </Modal>
    </Flex>
  );
};

export default UserManagement;
